/**
 * Custom React Hooks for Subscriptions Management
 */

import { useState, useCallback, useEffect, useReducer } from 'react';
import { subscriptionAPIClient } from '../services/SubscriptionAPIClient';
import {
  Subscription,
  SubscriptionPassword,
  PasswordEditState,
  SubscriptionItemState,
  SubscriptionsDashboardState,
  ApiError
} from '../types/SubscriptionTypes';

/**
 * Hook: useSubscriptions
 * Manages loading all subscriptions and their state
 */
export function useSubscriptions() {
  const [state, setState] = useState<SubscriptionsDashboardState>({
    subscriptions: [],
    subscriptionsByType: {
      'social-media': [],
      'legal-search': [],
      'database': [],
      'saas': [],
      'commercial-credit': []
    },
    selectedSubscriptionId: null,
    isLoading: true,
    error: null,
    expandedAccordions: new Set(),
    auditLog: []
  });

  useEffect(() => {
    loadSubscriptions();
  }, []);

  const loadSubscriptions = useCallback(async () => {
    setState(prev => ({ ...prev, isLoading: true, error: null }));
    try {
      const subscriptions = await subscriptionAPIClient.getAllSubscriptions();
      
      // Group by type
      const grouped = {
        'social-media': subscriptions.filter(s => s.type === 'social-media'),
        'legal-search': subscriptions.filter(s => s.type === 'legal-search'),
        'database': subscriptions.filter(s => s.type === 'database'),
        'saas': subscriptions.filter(s => s.type === 'saas'),
        'commercial-credit': subscriptions.filter(s => s.type === 'commercial-credit')
      };

      setState(prev => ({
        ...prev,
        subscriptions,
        subscriptionsByType: grouped,
        isLoading: false
      }));
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : 'Failed to load subscriptions';
      setState(prev => ({
        ...prev,
        error: errorMsg,
        isLoading: false
      }));
    }
  }, []);

  const toggleAccordion = useCallback((type: string) => {
    setState(prev => {
      const expanded = new Set(prev.expandedAccordions);
      if (expanded.has(type as any)) {
        expanded.delete(type as any);
      } else {
        expanded.add(type as any);
      }
      return { ...prev, expandedAccordions: expanded };
    });
  }, []);

  return { state, loadSubscriptions, toggleAccordion };
}

/**
 * Hook: useSubscriptionPassword
 * Manages password CRUD operations for a single subscription
 */
export function useSubscriptionPassword(subscriptionId: string) {
  const [passwordInfo, setPasswordInfo] = useState<SubscriptionPassword | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadPassword = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await subscriptionAPIClient.getPassword(subscriptionId);
      // Parse response and extract metadata
      // Response only contains masked password, username, and rotation info
      setPasswordInfo({
        subscriptionId,
        secretArn: '',
        username: response.data?.username,
        secretName: `nextcompli/subscriptions/${subscriptionId}/credentials`,
        lastRotated: response.data?.lastRotated ? new Date(response.data.lastRotated) : new Date(),
        nextRotation: response.data?.nextRotation ? new Date(response.data.nextRotation) : new Date(),
        rotationEnabled: true,
        rotationDays: 90
      });
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to load password';
      setError(errorMsg);
    } finally {
      setIsLoading(false);
    }
  }, [subscriptionId]);

  useEffect(() => {
    loadPassword();
  }, [loadPassword]);

  const createPassword = useCallback(
    async (username: string, password: string) => {
      setIsLoading(true);
      setError(null);
      try {
        await subscriptionAPIClient.createPassword(subscriptionId, username, password);
        await loadPassword(); // Refresh password info
        return true;
      } catch (err) {
        const errorMsg = err instanceof Error ? err.message : 'Failed to create password';
        setError(errorMsg);
        return false;
      } finally {
        setIsLoading(false);
      }
    },
    [subscriptionId, loadPassword]
  );

  const updatePassword = useCallback(
    async (newPassword: string, username?: string) => {
      setIsLoading(true);
      setError(null);
      try {
        await subscriptionAPIClient.updatePassword(subscriptionId, newPassword, username);
        await loadPassword(); // Refresh password info
        return true;
      } catch (err) {
        const errorMsg = err instanceof Error ? err.message : 'Failed to update password';
        setError(errorMsg);
        return false;
      } finally {
        setIsLoading(false);
      }
    },
    [subscriptionId, loadPassword]
  );

  const deletePassword = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      await subscriptionAPIClient.deletePassword(subscriptionId);
      setPasswordInfo(null);
      return true;
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to delete password';
      setError(errorMsg);
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [subscriptionId]);

  return {
    passwordInfo,
    showPassword,
    setShowPassword,
    isLoading,
    error,
    createPassword,
    updatePassword,
    deletePassword,
    loadPassword
  };
}

/**
 * Hook: usePasswordEditForm
 * Manages form state for password editing
 */
export function usePasswordEditForm(onSubmit: (username: string, password: string) => Promise<boolean>) {
  const [state, dispatch] = useReducer(
    (state: PasswordEditState, action: any) => {
      switch (action.type) {
        case 'START_EDIT':
          return { ...state, isEditing: true, error: null };
        case 'SET_USERNAME':
          return { ...state, username: action.payload };
        case 'SET_PASSWORD':
          return { ...state, newPassword: action.payload };
        case 'SET_CONFIRM_PASSWORD':
          return { ...state, confirmPassword: action.payload };
        case 'SET_ERROR':
          return { ...state, error: action.payload };
        case 'SET_LOADING':
          return { ...state, isLoading: action.payload };
        case 'CANCEL':
          return { ...state, isEditing: false, username: '', newPassword: '', confirmPassword: '', error: null };
        case 'RESET':
          return {
            isEditing: false,
            username: '',
            newPassword: '',
            confirmPassword: '',
            isLoading: false,
            error: null
          };
        default:
          return state;
      }
    },
    {
      isEditing: false,
      username: '',
      newPassword: '',
      confirmPassword: '',
      isLoading: false,
      error: null
    }
  );

  const validateForm = useCallback((): string | null => {
    if (!state.username.trim()) {
      return 'Username is required';
    }
    if (!state.newPassword) {
      return 'Password is required';
    }
    if (state.newPassword.length < 8) {
      return 'Password must be at least 8 characters';
    }
    if (state.newPassword !== state.confirmPassword) {
      return 'Passwords do not match';
    }
    return null;
  }, [state]);

  const handleSubmit = useCallback(async () => {
    const error = validateForm();
    if (error) {
      dispatch({ type: 'SET_ERROR', payload: error });
      return;
    }

    dispatch({ type: 'SET_LOADING', payload: true });
    try {
      const success = await onSubmit(state.username, state.newPassword);
      if (success) {
        dispatch({ type: 'RESET' });
      }
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  }, [state, validateForm, onSubmit]);

  const handleCancel = useCallback(() => {
    dispatch({ type: 'CANCEL' });
  }, []);

  return {
    state,
    dispatch,
    validateForm,
    handleSubmit,
    handleCancel
  };
}

/**
 * Hook: useSubscriptionItem
 * Manages expanded state and password editing for a single subscription item
 */
export function useSubscriptionItem(subscriptionId: string) {
  const [isExpanded, setIsExpanded] = useState(false);
  const passwordHook = useSubscriptionPassword(subscriptionId);

  const toggleExpanded = useCallback(() => {
    setIsExpanded(prev => !prev);
  }, []);

  return {
    isExpanded,
    toggleExpanded,
    ...passwordHook
  };
}

/**
 * Hook: usePasswordStrength
 * Validates password strength and provides feedback
 */
export function usePasswordStrength(password: string) {
  const [strength, setStrength] = useState({
    score: 0,
    feedback: [] as string[],
    label: 'Very Weak'
  });

  useEffect(() => {
    const validateAsync = async () => {
      if (!password) {
        setStrength({ score: 0, feedback: [], label: 'Very Weak' });
        return;
      }

      try {
        const result = await subscriptionAPIClient.validatePassword(password);
        const labels = ['Very Weak', 'Weak', 'Fair', 'Good', 'Very Strong'];
        setStrength({
          score: result.score,
          feedback: result.feedback,
          label: labels[result.score] || 'Unknown'
        });
      } catch (error) {
        // Fallback validation
        const score = calculateBasicStrength(password);
        const labels = ['Very Weak', 'Weak', 'Fair', 'Good', 'Very Strong'];
        setStrength({
          score,
          feedback: [],
          label: labels[score]
        });
      }
    };

    const timer = setTimeout(validateAsync, 300); // Debounce
    return () => clearTimeout(timer);
  }, [password]);

  return strength;
}

/**
 * Basic password strength calculation (fallback)
 */
function calculateBasicStrength(password: string): number {
  let score = 0;
  if (password.length >= 8) score++;
  if (password.length >= 12) score++;
  if (/[a-z]/.test(password) && /[A-Z]/.test(password)) score++;
  if (/\d/.test(password)) score++;
  if (/[^a-zA-Z\d]/.test(password)) score++;
  return Math.min(score, 4);
}
